import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { UpdatedTimestampPipe } from '../../../shared/pipes/UpdatedTimestampPipe';
import { AccountInfo } from '../../models';
import { Subject, combineLatest, map } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CreateTypes, pageInContext } from '../../../shared/utilities/Constants';
import { selectCancelModalContinueClicked, selectCancelModalIsQuicklinksNavigation, selectCreateTypeInContext, selectViewInContext } from '../../../shared/reducers';
import { selectTransactionType } from '../../../create-request/reducers';
import * as fromStandingAuthorizations from '../../../standing-authorizations/reducers';
import { displayCancelModal } from '../../../shared/actions/shared.actions';
import { selectMasterAccount, selectUserContext } from '../../reducers';
import { ContextType } from '@charlesschwab/as-ui-core';
import { ValidateWireFlagsService } from '../../../shared/services/validate-WireFlags.service';

@Component({
  standalone: false,
  selector: 'mm-account-info-header',
  templateUrl: './account-info-header.component.html',
  styleUrls: ['./account-info-header.component.scss']  
})

export class AccountInfoHeaderComponent implements AfterViewInit{
  @Input() accountInfo: AccountInfo | null;
  @Output() refresh = new EventEmitter<string>();

  step: string;
  createTypeInContext: CreateTypes;
  displayCancelModal: boolean;
  actionsPanel: Element;
  destroy$ = new Subject<void>();
  masterAccountId: string;
  contextType: string;

  constructor(private store: Store,
    private validateWireFlagsService: ValidateWireFlagsService
  ) { };

  ngOnInit(): void {

    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe((step) => {
      this.step = step;
    });

    this.store.select(selectCreateTypeInContext).pipe(takeUntil(this.destroy$)).subscribe((type) => {
      this.createTypeInContext = type;
    });

    this.store.select(selectMasterAccount).pipe(takeUntil(this.destroy$)).subscribe((masterAccountId) => {
      this.masterAccountId = masterAccountId;
    });

    this.store.select(selectUserContext).pipe(takeUntil(this.destroy$)).subscribe((contextType) => {
      this.contextType = contextType;
    });


    combineLatest([
      this.store.select(selectCancelModalContinueClicked),
      this.store.select(selectCancelModalIsQuicklinksNavigation)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([continueClicked, isQuicklinksNav]) => {
      if (!!continueClicked && !!isQuicklinksNav) {
        (this.actionsPanel as HTMLElement).click();
      }
    })
  }

  ngAfterViewInit(): void {
    // handle Action Quicklinks navigation
    combineLatest([
      this.store.select(selectTransactionType),
      this.store.select(fromStandingAuthorizations.selectTransactionType)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([transactionType, standingAuthTransactionType]) => {
      this.displayCancelModal = !!transactionType || !!standingAuthTransactionType;

      const actionsPanelList = document.getElementsByClassName('actions-panel');
      if (actionsPanelList) {
        this.actionsPanel = actionsPanelList[0];
        this.actionsPanel?.addEventListener('pointerdown', (e) => {
          if (this.displayCancelModal && (this.step === pageInContext.input || this.step === pageInContext.review)) {
            if (this.createTypeInContext === 'transaction') {
              this.store.dispatch(
                displayCancelModal({
                  nextUrlState: '',
                  title: 'Cancel transaction in progress',
                  content: ['Canceling will reset your transaction inputs. Are you sure you want to continue?'],
                  greenButton: 'Cancel transaction',
                  grayButton: 'Back',
                  isQuicklinksNav: true,
                  source: 'accountHeaderActionsDropdown'
                })
              );
            } else {
              this.store.dispatch(
                displayCancelModal({
                  nextUrlState: '',
                  title: 'Cancel this work in progress?',
                  content: ['The information you have entered to create this standing authorization will be lost.'],
                  greenButton: 'Cancel standing authorization',
                  grayButton: 'Back',
                  isQuicklinksNav: true,
                  source: 'accountHeaderActionsDropdown'
                })
              );
            }
          }
        })
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  get accountId(): string {
    return this.accountInfo?.accountId ?? '';
  }

  get updatedTimestamp(): string {
    return new UpdatedTimestampPipe().transform(this.accountInfo?.updated?? '');
  }

  get subAccountNameLine1(): string {
    return this.accountInfo?.subAccountNameLine1 ?? '';
  }

  get isAccountInContext(): boolean {
    return this.contextType == ContextType.Account 
    || (this.contextType == ContextType.PALAccount && this.validateWireFlagsService.enablePalWireCreateTransaction());
  }

  handleRefresh(): void {
    if(this.contextType == ContextType.Master) {
      this.refresh.emit(this.masterAccountId);
    }
    else if (this.contextType == ContextType.Account 
      || (this.contextType == ContextType.PALAccount && this.validateWireFlagsService.enablePalWireCreateTransaction())){
      this.refresh.emit(this.accountId);
    }
  }
}
